import { TaskCategories } from 'enums/taskCategories';
import type {
  SchedulingRecoveryDetail,
  SchedulingRecoveryList
} from 'models/schedulingRecovery.types';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isStuckReason = (value: unknown): value is SchedulingRecoveryList['stuckReason'] =>
  value === 'booking-failed' || value === 'no-slot-selected';

const isPatientType = (value: unknown): value is SchedulingRecoveryDetail['patientType'] =>
  value === 'new-patient' || value === 'returning-patient';

const isSlotState = (value: unknown): value is SchedulingRecoveryDetail['slotState'] =>
  value === 'held' || value === 'none';

const ISO_UTC_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?Z$/;

const isUtcDate = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;

  const match = ISO_UTC_DATE_PATTERN.exec(value);
  const timestamp = Date.parse(value);

  if (!match || !Number.isFinite(timestamp)) return false;

  const [, year, month, day, hour, minute, second, fraction = ''] = match;
  const date = new Date(timestamp);

  return (
    date.getUTCFullYear() === Number(year) &&
    date.getUTCMonth() + 1 === Number(month) &&
    date.getUTCDate() === Number(day) &&
    date.getUTCHours() === Number(hour) &&
    date.getUTCMinutes() === Number(minute) &&
    date.getUTCSeconds() === Number(second) &&
    date.getUTCMilliseconds() === Number(fraction.padEnd(3, '0'))
  );
};

export const readSchedulingRecoveryList = (value: unknown): SchedulingRecoveryList | undefined => {
  if (
    !isRecord(value) ||
    typeof value.programName !== 'string' ||
    !value.programName.trim() ||
    !isStuckReason(value.stuckReason) ||
    !isUtcDate(value.actionableAt)
  ) {
    return undefined;
  }

  return {
    programName: value.programName,
    stuckReason: value.stuckReason,
    // Keep the backend projection's documented field order for stable boundary snapshots.
    // eslint-disable-next-line perfectionist/sort-objects
    actionableAt: value.actionableAt
  };
};

export const readSchedulingRecoveryDetail = (
  value: unknown
): SchedulingRecoveryDetail | undefined => {
  const list = readSchedulingRecoveryList(value);

  if (
    !list ||
    !isRecord(value) ||
    !isPatientType(value.patientType) ||
    !isSlotState(value.slotState) ||
    !isUtcDate(value.checkoutCompletedAt) ||
    !isUtcDate(value.detectedAt)
  ) {
    return undefined;
  }

  return {
    ...list,
    checkoutCompletedAt: value.checkoutCompletedAt,
    detectedAt: value.detectedAt,
    patientType: value.patientType,
    slotState: value.slotState
  };
};

export const isSchedulingRecoveryTask = (task: {
  category?: string;
  schedulingRecovery?: unknown;
}): boolean =>
  task.category === TaskCategories.RequestAppointment &&
  !!readSchedulingRecoveryDetail(task.schedulingRecovery);

export const hasValidSchedulingRecoveryRevision = (revision: unknown): revision is number =>
  typeof revision === 'number' && Number.isInteger(revision);

export const isSchedulingRecoveryListTask = (task: {
  category?: string;
  schedulingRecovery?: unknown;
}): boolean =>
  task.category === TaskCategories.RequestAppointment &&
  !!readSchedulingRecoveryList(task.schedulingRecovery);

export const isSchedulingRecoveryTerminal = (status?: string): boolean =>
  ['completed', 'cancelled', 'deleted'].includes(status?.toLowerCase() ?? '');

export const SCHEDULING_RECOVERY_REASON_LABELS = {
  'booking-failed': 'Booking failed',
  'no-slot-selected': 'No slot selected'
} as const;

export const formatSchedulingRecoveryElapsed = (actionableAt: string, now = Date.now()): string => {
  const minutes = Math.max(0, Math.floor((now - Date.parse(actionableAt)) / 60_000));
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const remainder = minutes % 60;

  return `${days ? `${days}d ` : ''}${hours ? `${hours}h ` : ''}${remainder}m waiting`;
};
